import Vue from 'vue'
import proj4 from 'proj4'
import WKT from 'ol/format/WKT'
import MultiPolygon from 'ol/geom/MultiPolygon'
import { register as registerProj4 } from 'ol/proj/proj4'

/**
 * Maps a TerraIndex project (workingObject / CURRENT_PROJECT shape) onto the OVAM
 * grondverzetsonderzoek-melding request DTO expected by the DataWS OvamController.
 *
 * The project fields below come from the backend "New fields" change (dsFieldProject):
 * PrInitiativeTaker, PrOvamType, PrOvamPrecautionsRequiredCode, PrSoilInvestigationNotificationId,
 * PrMistralURL. `referentieEBSD` is entered by the user in the popup; the geometry is reprojected
 * to Belgian Lambert 2008 (see getProjectGeometry).
 */

// Constant integrator name, always sent as-is.
const INTEGRATOR = 'TerraIndex'

// Project field names (from the backend "New fields" change).
export const PROJECTZONE_TYPE_FIELD = 'PrOvamType'
export const REFERENTIE_INITIATIEFNEMER_FIELD = 'PrInitiativeTaker'

// The project contour and location point, both WKT in EPSG:4326. The contour is the projectzone the
// popup shows (and lets the user draw) and is the same field the project map draws into.
export const PROJECT_GEOMETRY_FIELD = 'PrPolygonGeometry'
export const PROJECT_POINT_FIELD = 'PrLocPointGeometry'
export const PROJECT_GEOMETRY_EPSG = 'EPSG:4326'

// OVAM-defined projectzone.type values (Open API Specs T&I).
export const PROJECTZONE_TYPE_OPTIONS = ['MILITAIR_DOMEIN', 'TUSSENTIJDSE_OPSLAGPLAATS', 'CENTRUM_VOOR_GRONDREINIGING', 'CENTRUM_VOOR_SLIBVERWERKING', 'ANDERE']

// "...Code" field backed by the normalized true/false code list (CODELOOKUP category 282:
// code 'true' -> "Ja", code 'false' -> "Nee"). The popup stores the code from that list and
// toBoolean maps it to the OVAM boolean vzmVeimNoodzakelijk.
export const VZM_VEIM_FIELD = 'PrOvamPrecautionsRequiredCode'
// CODELOOKUP CategoryID of the normalized true/false list used by PrOvamPrecautionsRequiredCode.
export const VZM_VEIM_CATEGORY_ID = 282
// Holds the melding id OVAM returns on create (its grondverzetsonderzoekMeldingId), used for update (PUT).
export const MELDING_ID_FIELD = 'PrSoilInvestigationNotificationId'
// Holds the Mistral URL OVAM returns on create.
export const MISTRAL_URL_FIELD = 'PrMistralURL'

/**
 * Coerces a project field value (may be a real boolean or a 'true'/'false' string) to a boolean.
 * @param {*} value
 * @returns {boolean}
 */
export function toBoolean(value) {
  if (value === true || value === 1) {
    return true
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    return normalized === 'true' || normalized === '1'
  }
  return false
}

/**
 * The stored melding id, used for the update (PUT) call. Empty until a POST has succeeded.
 * @param {object} project
 * @returns {string}
 */
export function getMeldingId(project) {
  const value = project && project[MELDING_ID_FIELD]
  return value != null && value !== '' ? value : ''
}

/**
 * Stores the melding id returned by a POST onto the project.
 * Uses Vue.set: PrSoilInvestigationNotificationId does not exist on the project object until the
 * backend "new-fields" change lands, so a plain assignment would add a new, non-reactive property -
 * canUpdate (and the Updaten button) would never react to it.
 * TODO: persist this on the project via the project save flow.
 * @param {object} project
 * @param {string|number} id
 */
export function setMeldingId(project, id) {
  if (project) {
    // PrSoilInvestigationNotificationId is an int field in the backend, so store a number.
    const numeric = id != null && id !== '' ? Number(id) : null
    Vue.set(project, MELDING_ID_FIELD, Number.isNaN(numeric) ? null : numeric)
  }
}

/**
 * Stores the Mistral URL returned by a POST onto the project.
 * Uses Vue.set for the same reactivity reason as setMeldingId (PrMistralURL is a new field too).
 * TODO: persist this on the project via the project save flow.
 * @param {object} project
 * @param {string} mistralUrl
 */
export function setMistralUrl(project, mistralUrl) {
  if (project) {
    Vue.set(project, MISTRAL_URL_FIELD, mistralUrl || '')
  }
}

// OVAM expects the projectzone geometry in Belgian Lambert 2008 (EPSG:3812) as "SRID=3812;<WKT>".
// Project geometry (PrPolygonGeometry) is stored as WKT in EPSG:4326 (WGS84), so we reproject it.
export const OVAM_EPSG = 'EPSG:3812'
const OVAM_SRID = 3812
const OVAM_PROJ4_DEF =
  '+proj=lcc +lat_1=49.83333333333334 +lat_2=51.16666666666666 +lat_0=50.797815 +lon_0=4.359215833333333 +x_0=649328 +y_0=665262 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'

let ovamProjectionRegistered = false
function ensureOvamProjection() {
  if (ovamProjectionRegistered) {
    return
  }
  proj4.defs(OVAM_EPSG, OVAM_PROJ4_DEF)
  registerProj4(proj4)
  ovamProjectionRegistered = true
}

/**
 * Reprojects the project's polygon contour (WKT in EPSG:4326) to the OVAM projectzone format:
 * "SRID=3812;MULTIPOLYGON (...)" in Belgian Lambert 2008. Returns '' when there is no contour.
 * @param {object} project
 * @returns {string}
 */
export function getProjectGeometry(project) {
  const p = project || {}
  const polygon = p[PROJECT_GEOMETRY_FIELD]
  if (!polygon || !polygon.trim() || polygon.trim().toUpperCase() === 'POLYGON EMPTY') {
    return ''
  }

  ensureOvamProjection()
  const format = new WKT()
  // Strip any existing "SRID=...;" prefix before parsing.
  const wkt = polygon.replace(/^\s*SRID=\d+\s*;\s*/i, '')
  let geometry = format.readGeometry(wkt)
  geometry.transform(PROJECT_GEOMETRY_EPSG, OVAM_EPSG)

  // OVAM's projectzone expects a MULTIPOLYGON.
  if (geometry.getType() === 'Polygon') {
    geometry = new MultiPolygon([geometry.getCoordinates()])
  }

  return `SRID=${OVAM_SRID};${format.writeGeometry(geometry)}`
}

/**
 * Parses an OVAM projectzone geometry ("SRID=3812;<WKT>") back into an OpenLayers geometry in
 * OVAM_EPSG, so it can be shown on a map. Returns null when there is nothing (parsable) to show.
 * @param {string} geometrie
 * @returns {import('ol/geom/Geometry').default|null}
 */
export function readProjectzoneGeometry(geometrie) {
  if (!geometrie || !String(geometrie).trim()) {
    return null
  }

  ensureOvamProjection()
  const wkt = String(geometrie).replace(/^\s*SRID=\d+\s*;\s*/i, '')
  try {
    return new WKT().readGeometry(wkt)
  } catch (error) {
    return null
  }
}

/**
 * Writes a geometry drawn on the map back into the project contour format: WKT in EPSG:4326.
 * @param {import('ol/geom/Geometry').default} geometry
 * @param {string} sourceProjection the projection the drawn geometry is in (the map's view)
 * @returns {string}
 */
export function writeProjectGeometry(geometry, sourceProjection) {
  let clone = geometry.clone()
  clone.transform(sourceProjection, PROJECT_GEOMETRY_EPSG)

  // Editing happens on the MULTIPOLYGON we hand to the map, but a zone of one part is stored as a
  // POLYGON: the project map can only edit a contour that is a single polygon (see drawHandlers).
  // Separate parcels stay a MULTIPOLYGON.
  if (clone.getType() === 'MultiPolygon' && clone.getPolygons().length === 1) {
    clone = clone.getPolygon(0)
  }

  // OpenLayers writes "POLYGON((...))" while the contour drawn on the project map and the stored
  // project data use "POLYGON ((...))"; keep that spacing so we write what the rest of the app writes.
  return new WKT().writeGeometry(clone).replace('(', ' (')
}

/**
 * Stores a drawn contour on the project. Uses Vue.set because PrPolygonGeometry can come back as
 * null (see the project DTO), and both the request and the map have to react to a drawn zone.
 * @param {object} project
 * @param {string} wkt contour as WKT in EPSG:4326, or '' to remove it
 */
export function setProjectGeometry(project, wkt) {
  if (project) {
    Vue.set(project, PROJECT_GEOMETRY_FIELD, wkt || '')
  }
}

/**
 * The project's location point as [lon, lat], used to center the map when there is no contour to
 * fit yet. Returns null when the project has no (parsable) point.
 * @param {object} project
 * @returns {number[]|null}
 */
export function getProjectPoint(project) {
  const point = project && project[PROJECT_POINT_FIELD]
  if (!point || !String(point).trim()) {
    return null
  }
  try {
    const geometry = new WKT().readGeometry(String(point).replace(/^\s*SRID=\d+\s*;\s*/i, ''))
    return geometry.getType() === 'Point' ? geometry.getCoordinates() : null
  } catch (error) {
    return null
  }
}

/**
 * Builds the GrondverzetsonderzoekMeldingRequest body from the project.
 * @param {object} project workingObject / CURRENT_PROJECT
 * @param {object} overrides values entered in the popup (e.g. referentieEBSD)
 * @returns {object}
 */
export function buildMeldingRequest(project, overrides = {}) {
  const p = project || {}
  return {
    titel: overrides.titel || p.PrName || '', // project name
    referentieEBSD: overrides.referentieEBSD || '', // filled in by the user in the popup
    referentieInitiatiefnemer: p[REFERENTIE_INITIATIEFNEMER_FIELD] || '',
    referentieIntegrator: INTEGRATOR, // constant
    vzmVeimNoodzakelijk: toBoolean(p[VZM_VEIM_FIELD]),
    projectzone: {
      type: p[PROJECTZONE_TYPE_FIELD] || '',
      geometrie: getProjectGeometry(project)
    }
  }
}
